import { useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react'
import clsx from 'clsx'

export interface StudentDetailTab {
  id: string
  label: string
  /** 활성일 때만 호출 — self-fetch 패널 동시 마운트 방지 */
  render: () => ReactNode
}

interface Props {
  tabs: StudentDetailTab[]
  ariaLabel: string
  initialId?: string
  activeId?: string
  onActiveIdChange?: (id: string) => void
}

export function StudentDetailTabs({ tabs, ariaLabel, initialId, activeId: controlledActiveId, onActiveIdChange }: Props) {
  const baseId = useId()
  const [uncontrolledActiveId, setUncontrolledActiveId] = useState(initialId ?? tabs[0]?.id)
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({})
  const activeId = controlledActiveId ?? uncontrolledActiveId
  const setActiveId = (id: string) => {
    if (controlledActiveId == null) {
      setUncontrolledActiveId(id)
    }
    onActiveIdChange?.(id)
  }

  const activeIndex = Math.max(0, tabs.findIndex((tb) => tb.id === activeId))
  const active = tabs[activeIndex]

  const moveTo = (idx: number) => {
    if (tabs.length === 0) return
    const next = tabs[(idx + tabs.length) % tabs.length]
    setActiveId(next.id)
    tabRefs.current[next.id]?.focus()
  }

  const onKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        e.preventDefault(); moveTo(activeIndex + 1); break
      case 'ArrowLeft':
      case 'ArrowUp':
        e.preventDefault(); moveTo(activeIndex - 1); break
      case 'Home':
        e.preventDefault(); moveTo(0); break
      case 'End':
        e.preventDefault(); moveTo(tabs.length - 1); break
    }
  }

  return (
    <div>
      <div role="tablist" aria-label={ariaLabel} className="flex flex-wrap gap-1 border-b border-gray-200">
        {tabs.map((tb) => {
          const selected = tb.id === active?.id
          return (
            <button
              key={tb.id}
              ref={(el) => { tabRefs.current[tb.id] = el }}
              role="tab"
              type="button"
              id={`${baseId}-tab-${tb.id}`}
              aria-selected={selected}
              aria-controls={selected ? `${baseId}-panel-${tb.id}` : undefined}
              tabIndex={selected ? 0 : -1}
              onClick={() => setActiveId(tb.id)}
              onKeyDown={onKeyDown}
              className={clsx(
                '-mb-px px-4 py-2 text-sm font-medium border-b-2',
                selected
                  ? 'border-primary-500 text-primary-700'
                  : 'border-transparent text-gray-500 hover:text-gray-700',
              )}
            >
              {tb.label}
            </button>
          )
        })}
      </div>
      {active && (
        <div
          role="tabpanel"
          id={`${baseId}-panel-${active.id}`}
          aria-labelledby={`${baseId}-tab-${active.id}`}
          tabIndex={0}
          className="pt-4 focus:outline-none"
        >
          {active.render()}
        </div>
      )}
    </div>
  )
}
